import { TextStyle, TextStyleOptions } from '@tiptap/extension-text-style'
import { CommentStorage } from '../../types'
import { joinCssDeclarations } from '../helpers'
import { IS_NON_MANUAL_TEXT_UPDATE, NON_MANUAL_UPDATE_TYPE } from '../../constants'
import { CustomAttributes } from '../types'
import { UnderlineColors, UnderlineStyles } from './constants'

export const CustomUnderline = TextStyle.configure().extend<
  TextStyleOptions,
  CommentStorage
>({
  name: 'customunderline',
  inclusive: false,

  addStorage() {
    return {
      comments: {},
    }
  },

  addCommands() {
    return {
      setCustomUnderline: (attributes) => ({ chain }) => {
        this.storage.comments[attributes.id] = true

        return chain()
          .setMeta(IS_NON_MANUAL_TEXT_UPDATE, true)
          .setTextSelection({
            from: attributes.offset,
            to: attributes.offset + attributes.length,
          })
          .setMark(this.name, {
            id: attributes.id,
            type: attributes.type,
          })
          .setTextSelection({ from: 0, to: 0 })
          .focus()
          .run()
      },

      removeCustomUnderline: (attributes) => ({ chain, tr: transaction, state }) => {
        delete this.storage.comments[attributes.id]

        const mark = state.schema.mark(this.name, { id: attributes.id })
        transaction.removeStoredMark(mark)

        return chain()
          .setMeta(IS_NON_MANUAL_TEXT_UPDATE, true)
          .setTextSelection({
            from: attributes.offset,
            to: attributes.offset + attributes.length,
          })
          .unsetMark(mark.type)
          .setTextSelection({ from: 0, to: 0 })
          .focus()
          .run()
      },
      removeCustomUnderlineText: (attributes) => ({ chain, tr: transaction, state }) => {
        delete this.storage.comments[attributes.id]

        const from = attributes.offset
        const to = attributes.offset + attributes.length
        const mark = state.schema.mark(this.name, { id: attributes.id })
        transaction.removeMark(from, to, mark)
        transaction.removeStoredMark(mark)

        return chain()
          .setMeta(IS_NON_MANUAL_TEXT_UPDATE, true)
          .setMeta(NON_MANUAL_UPDATE_TYPE, attributes.nonManualUpdateType)
          .setTextSelection({ from, to })
          .deleteRange({ from, to })
          .run()
      },
      replaceCustomUnderlineText: (attributes, replacement) => ({
        chain,
        tr: transaction,
        state,
      }) => {
        delete this.storage.comments[attributes.id]

        const from = attributes.offset
        const to = attributes.offset + attributes.length
        const mark = state.schema.mark(this.name, { id: attributes.id })
        transaction.removeMark(from, to, mark)
        transaction.removeStoredMark(mark)

        return chain()
          .setMeta(IS_NON_MANUAL_TEXT_UPDATE, true)
          .setMeta(NON_MANUAL_UPDATE_TYPE, attributes.nonManualUpdateType)
          .setTextSelection({ from, to })
          .insertContentAt({ from, to }, replacement)
          .run()
      },
    }
  },

  addAttributes() {
    return {
      type: {
        default: null,
      },

      'border-bottom': {
        renderHTML: (attributes: CustomAttributes) => {
          const underlineColor = UnderlineColors[attributes.type]
          return {
            style: joinCssDeclarations([
              ...Object.values(UnderlineStyles),
              `border-bottom-color:${underlineColor}`,
            ]),
          }
        },
      },
      id: {
        default: null,
      },
      accessibility: {
        default: null,
        renderHTML: (attributes: CustomAttributes) => ({
          tabindex: '0',
          role: 'button',
          'data-test': `customunderline-mark-${attributes.id}`,
        }),
      },
    }
  },
})
